import {useCallback, useMemo, useState} from 'react'
import {ScrollView, StyleSheet, View} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {SOCIAL_DEFAULT_SERVICE} from '#/lib/constants'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {cleanError, isNetworkError} from '#/lib/strings/errors'
import {createFullHandle} from '#/lib/strings/handles'
import {normalizeAtprotoIdentifier} from '#/lib/strings/identifiers'
import {useServiceQuery} from '#/state/queries/service'
import {useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {HostingProvider} from '#/components/forms/HostingProvider'
import * as TextField from '#/components/forms/TextField'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

const DEFAULT_BIRTHDATE = new Date(Date.now() - 60e3 * 60 * 24 * 365 * 20)

function normalizeServiceUrl(input: string, fallback: string) {
  const trimmed = input.trim()
  if (!trimmed) return fallback
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function formatServiceError(err: unknown) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  if (offline) return 'Aucune connexion Internet.'
  if (isNetworkError(err)) {
    return 'Service temporairement indisponible. Réessayez.'
  }
  return cleanError(err) || 'Impossible de créer le compte.'
}

export function SignupOneShot({onPressBack}: {onPressBack: () => void}) {
  const {_} = useLingui()
  const t = useTheme()
  const insets = useSafeAreaInsets()
  const {isDesktop} = useWebMediaQueries()
  const {createAccount} = useSessionApi()
  const {setShowLoggedOut} = useLoggedOutViewControls()

  const [serviceUrl, setServiceUrl] = useState(SOCIAL_DEFAULT_SERVICE)
  const [handle, setHandle] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {data: serviceInfo, isFetching, isError} = useServiceQuery(serviceUrl)

  const serviceErrorMessage = useMemo(() => {
    const offline =
      typeof navigator !== 'undefined' && navigator.onLine === false
    return offline
      ? 'Aucune connexion Internet.'
      : 'Service temporairement indisponible. Réessayez.'
  }, [])

  const userDomain = serviceInfo?.availableUserDomains?.[0] || ''
  const inviteRequired = serviceInfo?.inviteCodeRequired

  const canSubmit =
    !isSubmitting &&
    !isFetching &&
    !isError &&
    handle.trim().length > 0 &&
    password.length > 0

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return
    setError('')

    const cleanHandle = handle.trim().replace(/^@+/, '')
    if (!cleanHandle) {
      setError('Entrez votre pseudo.')
      return
    }

    if (inviteRequired && !inviteCode.trim()) {
      setError('Entrez votre code d’invitation.')
      return
    }

    let fullHandle = cleanHandle
    if (!cleanHandle.includes('.')) {
      if (userDomain) {
        fullHandle = createFullHandle(cleanHandle, userDomain)
      } else {
        fullHandle = normalizeAtprotoIdentifier(cleanHandle, serviceUrl)
      }
    }

    setIsSubmitting(true)
    try {
      await createAccount({
        service: normalizeServiceUrl(serviceUrl, SOCIAL_DEFAULT_SERVICE),
        email: email.trim(),
        handle: fullHandle,
        password,
        birthDate: DEFAULT_BIRTHDATE,
        inviteCode: inviteCode.trim(),
      })
      setShowLoggedOut(false)
    } catch (err) {
      setError(formatServiceError(err))
    } finally {
      setIsSubmitting(false)
    }
  }, [
    canSubmit,
    handle,
    inviteRequired,
    inviteCode,
    userDomain,
    serviceUrl,
    createAccount,
    email,
    password,
    setShowLoggedOut,
  ])

  const containerStyle = useMemo(
    () => [
      styles.container,
      t.atoms.bg,
      {paddingTop: insets.top + 24, paddingBottom: insets.bottom + 24},
      isDesktop && styles.containerDesktop,
    ],
    [t, insets, isDesktop],
  )

  return (
    <ScrollView contentContainerStyle={containerStyle}>
      <View style={styles.inner}>
        <View style={[a.gap_sm]}>
          <Text style={[styles.title, t.atoms.text]}>
            <Trans>Créer votre compte Tellus</Trans>
          </Text>
          <Text style={[styles.subtitle, t.atoms.text_contrast_medium]}>
            <Trans>Une seule étape, puis activation.</Trans>
          </Text>
        </View>

        <View style={[styles.card, t.atoms.bg]}>
          <View style={[a.gap_sm]}>
            {error ? (
              <View style={[styles.errorWrap]}>
                <Text
                  style={[styles.errorText, {color: t.palette.negative_600}]}>
                  {error}
                </Text>
              </View>
            ) : null}
            <View>
              <TextField.LabelText>
                <Trans>Pseudo</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={handle}
                  onChangeText={setHandle}
                  label={_(msg`Pseudo`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </TextField.Root>
              {handle.trim() ? (
                <Text style={[styles.handleHint, t.atoms.text_contrast_medium]}>
                  <Trans>Affiché comme</Trans> @
                  {handle.trim().replace(/^@+/, '')}
                </Text>
              ) : null}
            </View>
            <View>
              <TextField.LabelText>
                <Trans>Mot de passe</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={password}
                  onChangeText={setPassword}
                  label={_(msg`Mot de passe`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry
                />
              </TextField.Root>
            </View>
            <View>
              <TextField.LabelText>
                <Trans>Email (optionnel)</Trans>
              </TextField.LabelText>
              <TextField.Root>
                <TextField.Input
                  value={email}
                  onChangeText={setEmail}
                  label={_(msg`Email`)}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                />
              </TextField.Root>
            </View>

            {showAdvanced ? (
              <View style={[a.gap_sm]}>
                <Text
                  style={[styles.advancedTitle, t.atoms.text_contrast_medium]}>
                  <Trans>Connexion (avancé)</Trans>
                </Text>
                <HostingProvider
                  minimal
                  serviceUrl={serviceUrl}
                  onSelectServiceUrl={setServiceUrl}
                />
                {inviteRequired ? (
                  <View>
                    <TextField.LabelText>
                      <Trans>Code d’invitation</Trans>
                    </TextField.LabelText>
                    <TextField.Root>
                      <TextField.Input
                        value={inviteCode}
                        onChangeText={setInviteCode}
                        label={_(msg`Code d’invitation`)}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                    </TextField.Root>
                  </View>
                ) : null}
              </View>
            ) : null}

            <Button
              label={_(msg`À propos / avancé`)}
              color="secondary"
              size="small"
              variant="ghost"
              onPress={() => setShowAdvanced(v => !v)}
              style={[a.self_start]}>
              <ButtonText>
                <Trans>À propos / avancé</Trans>
              </ButtonText>
            </Button>

            {isFetching ? (
              <Text style={[styles.helper, t.atoms.text_contrast_medium]}>
                <Trans>Connexion au service...</Trans>
              </Text>
            ) : null}
            {isError ? (
              <Text style={[styles.helper, {color: t.palette.negative_600}]}>
                {serviceErrorMessage}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={[styles.buttonRow]}>
          <Button
            label={_(msg`Retour`)}
            color="secondary"
            size="large"
            variant="outline"
            onPress={onPressBack}
            style={styles.secondaryButton}>
            <ButtonText>
              <Trans>Retour</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Continuer`)}
            color="primary"
            size="large"
            variant="solid"
            disabled={!canSubmit}
            onPress={onSubmit}
            style={styles.primaryButton}>
            <ButtonText>
              <Trans>Continuer</Trans>
            </ButtonText>
            {isSubmitting ? <ButtonIcon icon={Loader} /> : null}
          </Button>
        </View>
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
  },
  containerDesktop: {
    paddingHorizontal: 32,
  },
  inner: {
    maxWidth: 420,
    width: '100%',
    alignSelf: 'center',
    gap: 16,
  },
  title: {
    fontSize: 32,
    fontWeight: '800',
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 22,
    opacity: 0.7,
  },
  card: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.06)',
    padding: 18,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 2},
    elevation: 1,
  },
  errorWrap: {
    borderRadius: 12,
    backgroundColor: 'rgba(255, 0, 0, 0.05)',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  errorText: {
    fontSize: 13,
  },
  handleHint: {
    fontSize: 12,
    marginTop: 6,
  },
  advancedTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  helper: {
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
  },
  primaryButton: {
    flex: 1,
    height: 52,
    borderRadius: 16,
  },
  secondaryButton: {
    height: 52,
    borderRadius: 16,
  },
})
